import { useEffect, useLayoutEffect, useRef } from 'react'

/** A textarea that grows with its text, so nothing is hidden behind a scroll:
 *  like the slide, every word is on show. */
export default function AutoGrow({ value, onChange, ...rest }: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> & {
  value: string
  onChange: (value: string) => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const fit = () => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 2}px`
  }
  useLayoutEffect(fit, [value])
  useEffect(() => {
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [])
  return <textarea ref={ref} rows={2} value={value} onChange={e => onChange(e.target.value)} {...rest} />
}

/** The DDI text box. Dashed while empty, so an unwritten row stands out in
 *  the list; a check flag from the AI draft turns the edge amber. */
export const ddiFieldClass = (empty: boolean, flagged = false) =>
  `w-full px-3 py-2 rounded-lg text-sm leading-relaxed resize-none overflow-hidden focus:outline-none focus:border-[#00ACED] focus:ring-2 focus:ring-[#00ACED]/20 disabled:bg-slate-50 disabled:text-slate-500 border ${
    flagged ? 'border-[#F88806]/60 bg-[#F88806]/[0.04]'
      : empty ? 'border-dashed border-slate-300 bg-slate-50/60'
        : 'border-slate-200'}`
